import { cn } from '@finn/ui-utils';
import {
  forwardRef,
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';

const Table = forwardRef<
  HTMLTableElement,
  HTMLAttributes<HTMLTableElement> & { containerClassname?: string }
>(({ className, containerClassname, ...props }, ref) => (
  <div className={cn('relative w-full overflow-auto', containerClassname)}>
    <table
      ref={ref}
      className={cn(
        'w-full caption-bottom border-separate border-spacing-0 rounded-t border border-solid border-pearl',
        className
      )}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      // we want to keep row highlighted if smth expanded inside of it
      '[&_tr:has(button[aria-expanded=true])]:bg-snow',
      className
    )}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot ref={ref} className={className} {...props} />
));
TableFooter.displayName = 'TableFooter';

const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement> & {
    status?: 'ok' | 'error';
    href?: string;
    onClick?: () => void;
  }
>(({ className, status, children, onClick, ...props }, ref) => {
  // We tried to migrate table to smth else, like grid based or using table-row/cell components
  // but it was too much work and too much hacks, especially for colSpan and rowSpan
  // so we decided to keep it as is, and for onClick case we just add role and tabIndex
  const linkProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
      }
    : undefined;

  return (
    <tr
      ref={ref}
      className={cn(
        'group relative transition-colors hover:bg-snow',
        className
      )}
      {...linkProps}
      {...props}
    >
      {children}
      {status ? (
        <div
          className={cn(
            'absolute inset-y-2.5 left-0 w-1',
            status === 'error' ? 'bg-red' : 'bg-trustedBlue'
          )}
        />
      ) : null}
    </tr>
  );
});
TableRow.displayName = 'TableRow';

const TableHead = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    scope="col"
    ref={ref}
    className={cn(
      // layout
      'px-4 py-6 text-left',
      // colors and border
      'border-0 border-b border-solid border-pearl bg-cotton',
      // typography
      'truncate body-12-semibold',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      // layout
      'relative px-4 py-6 text-left align-middle',
      // colors and border
      'border-0 border-b border-solid border-pearl group-last:border-b-0',
      // typography
      'body-14-regular',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn(
      'border border-t-0 border-solid border-pearl p-4 text-left',
      className
    )}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
